/**
 * PlacesRepository — places metadata plus private image storage.
 *
 * Storage contract (spec §10.7 / §14.4):
 * - private bucket `place-images`, objects at
 *   `place-images/<user_id>/<place_id>/<uuid>.<ext>`;
 * - validation: image MIME type, allowed extension, size ≤ 5 MB;
 * - display URLs are short-lived signed URLs (SIGNED_URL_TTL_SECONDS) —
 *   `getPublicUrl` is NEVER used for private objects;
 * - orphaned objects are removed on image replace, image removal and place
 *   deletion (best-effort cleanup after the confirmed DB write).
 */
import { BUCKETS, requireSupabase, SIGNED_URL_TTL_SECONDS } from '@/lib/supabase/client'
import {
  placeFromRow,
  placeToInsert,
  placeToPatch,
  type PlaceRow,
} from '@/lib/supabase/mappers'
import type { Place } from '@/types'
import type { PlacesRepository } from '../interfaces'
import { assertRowReturned, fail, newObjectId, notFoundError, requireUserId, validationError } from './helpers'

const MAX_IMAGE_BYTES = 5 * 1024 * 1024

const EXTENSION_BY_MIME: Record<string, string> = {
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/heic': 'heic',
  'image/heif': 'heif',
}

const ALLOWED_EXTENSIONS = new Set(['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif'])

function extensionFromName(name: string): string {
  const match = /\.([a-z0-9]+)$/i.exec(name)
  return match ? match[1].toLowerCase() : ''
}

/** Validates type/extension/size. Throws AppError (source 'validation'). */
export function validatePlaceImageFile(file: File): { extension: string } {
  const mime = file.type.toLowerCase()
  const nameExtension = extensionFromName(file.name)
  const extension = EXTENSION_BY_MIME[mime] ?? (ALLOWED_EXTENSIONS.has(nameExtension) ? nameExtension : '')

  if (!mime.startsWith('image/') && !ALLOWED_EXTENSIONS.has(nameExtension)) {
    throw validationError(
      'IMAGE_TYPE_INVALID',
      'Dit bestand is geen foto. Kies een afbeelding.',
      `type=${file.type || 'unknown'} name=${file.name}`,
    )
  }
  if (!extension) {
    throw validationError(
      'IMAGE_EXTENSION_INVALID',
      'Dit fotoformaat wordt niet ondersteund. Gebruik jpg, png of webp.',
      `type=${file.type || 'unknown'} ext=${nameExtension || 'none'}`,
    )
  }
  if (file.size > MAX_IMAGE_BYTES) {
    throw validationError(
      'IMAGE_TOO_LARGE',
      'De foto is groter dan 5 MB. Kies een kleinere foto.',
      `size=${file.size}`,
    )
  }
  if (file.size === 0) {
    throw validationError('IMAGE_EMPTY', 'Deze foto is leeg. Kies een andere foto.')
  }
  return { extension }
}

async function fetchPlaceOrThrow(placeId: string, userId: string): Promise<Place> {
  const supabase = requireSupabase()
  const { data, error } = await supabase
    .from('places')
    .select('*')
    .eq('id', placeId)
    .eq('user_id', userId)
    .maybeSingle()
  if (error) fail(error)
  if (!data) fail(notFoundError('Deze plaats bestaat niet of je hebt er geen toegang toe.'))
  return placeFromRow(data as PlaceRow)
}

/** Best-effort orphan cleanup; never masks a confirmed write with a failure. */
async function removeObjectBestEffort(path: string): Promise<void> {
  try {
    const supabase = requireSupabase()
    const { error } = await supabase.storage.from(BUCKETS.placeImages).remove([path])
    if (error) console.warn('[places] orphan cleanup failed', error.message)
  } catch {
    // cleanup failure must not fail the user action
  }
}

export const placesRepository: PlacesRepository = {
  async list() {
    const supabase = requireSupabase()
    const userId = await requireUserId()
    const { data, error } = await supabase
      .from('places')
      .select('*')
      .eq('user_id', userId)
      .order('updated_at', { ascending: false })
    if (error) fail(error)
    return ((data ?? []) as PlaceRow[]).map(placeFromRow)
  },

  async create(place) {
    const supabase = requireSupabase()
    const userId = await requireUserId()
    const { data, error } = await supabase
      .from('places')
      .insert(placeToInsert(place, userId))
      .select()
      .single()
    return placeFromRow(assertRowReturned(data, error) as PlaceRow)
  },

  async update(id, patch) {
    const supabase = requireSupabase()
    const userId = await requireUserId()
    const { data, error } = await supabase
      .from('places')
      .update(placeToPatch(patch))
      .eq('id', id)
      .eq('user_id', userId)
      .select()
      .single()
    return placeFromRow(assertRowReturned(data, error) as PlaceRow)
  },

  async remove(id) {
    const supabase = requireSupabase()
    const userId = await requireUserId()
    const existing = await fetchPlaceOrThrow(id, userId)
    const { error } = await supabase.from('places').delete().eq('id', id).eq('user_id', userId)
    if (error) fail(error)
    // Orphaned-object cleanup after the confirmed delete.
    if (existing.imagePath) await removeObjectBestEffort(existing.imagePath)
  },

  async uploadImage(placeId, file) {
    const supabase = requireSupabase()
    const userId = await requireUserId()
    const place = await fetchPlaceOrThrow(placeId, userId)
    const { extension } = validatePlaceImageFile(file)

    const objectPath = `${userId}/${placeId}/${newObjectId()}.${extension}`
    const { error: uploadError } = await supabase.storage
      .from(BUCKETS.placeImages)
      .upload(objectPath, file, { contentType: file.type || undefined, upsert: false })
    if (uploadError) fail(uploadError, 'storage')

    const previousPath = place.imagePath
    const { data, error } = await supabase
      .from('places')
      .update({ image_path: objectPath })
      .eq('id', placeId)
      .eq('user_id', userId)
      .select()
      .single()

    if (error || !data) {
      // The row write failed: remove the just-uploaded object so the bucket
      // never accumulates unreferenced files.
      await removeObjectBestEffort(objectPath)
      fail(error ?? notFoundError())
    }

    // Replace semantics: the old object is now orphaned; clean it up.
    if (previousPath && previousPath !== objectPath) await removeObjectBestEffort(previousPath)
    return placeFromRow(data as PlaceRow)
  },

  async removeImage(placeId) {
    const supabase = requireSupabase()
    const userId = await requireUserId()
    const place = await fetchPlaceOrThrow(placeId, userId)
    const previousPath = place.imagePath

    const { data, error } = await supabase
      .from('places')
      .update({ image_path: null })
      .eq('id', placeId)
      .eq('user_id', userId)
      .select()
      .single()
    const updated = placeFromRow(assertRowReturned(data, error) as PlaceRow)
    if (previousPath) await removeObjectBestEffort(previousPath)
    return updated
  },

  async getSignedImageUrl(place) {
    if (!place.imagePath) return null
    const supabase = requireSupabase()
    await requireUserId()
    const { data, error } = await supabase.storage
      .from(BUCKETS.placeImages)
      .createSignedUrl(place.imagePath, SIGNED_URL_TTL_SECONDS)
    if (error) fail(error, 'storage')
    return data?.signedUrl ?? null
  },
}
